import React from "react"

interface DebugTabProps {
  debugInfo?: {
    userId: string
    userEmail: string
    projectsCount: number
    tasksCount: number
    timestamp: string
  }
  projectsData?: {
    items: any[]
    hasMore?: boolean
    nextCursor?: string
  }
  tasksData?: {
    items: any[]
    hasMore?: boolean
    nextCursor?: string
  }
  searchResults?: {
    items: any[]
    total: number
  }
}

const DebugTab: React.FC<DebugTabProps> = ({ debugInfo, projectsData, tasksData, searchResults }) => {
  const formatJson = (data: any) => {
    return JSON.stringify(data, null, 2)
  }

  return (
    <div className="space-y-6">
      {/* System Information */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">🔍 System Information</h3>
        {debugInfo ? (
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-muted-foreground">User ID:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{debugInfo.userId}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">User Email:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{debugInfo.userEmail}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Projects Count:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{debugInfo.projectsCount}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Tasks Count:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{debugInfo.tasksCount}</div>
              </div>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Last Updated:</span>
              <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
                {new Date(debugInfo.timestamp).toLocaleString()}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground">Loading debug information...</div>
        )}
      </div>

      {/* Projects Data */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">📋 Projects Data</h3>
        {projectsData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Total Projects:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{projectsData.items?.length || 0}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Has More Pages:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{projectsData.hasMore ? "Yes" : "No"}</div>
              </div>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Raw Data:</span>
              <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-40 mt-1">{formatJson(projectsData)}</pre>
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground">No projects data available</div>
        )}
      </div>

      {/* Tasks Data */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">✅ Tasks Data</h3>
        {tasksData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Total Tasks:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{tasksData.items?.length || 0}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Status Breakdown:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
                  {tasksData.items
                    ? (() => {
                        const statusCounts = tasksData.items.reduce((acc: any, task: any) => {
                          const status = ["Todo", "In Progress", "Done"][task.status] || "Unknown"
                          acc[status] = (acc[status] || 0) + 1
                          return acc
                        }, {})
                        return Object.entries(statusCounts)
                          .map(([status, count]) => `${status}: ${JSON.stringify(count)}`)
                          .join(", ")
                      })()
                    : "No data"}
                </div>
              </div>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Raw Data:</span>
              <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-40 mt-1">{formatJson(tasksData)}</pre>
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground">No tasks data available (select a project first)</div>
        )}
      </div>

      {/* Search Results Data */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">🔍 Search Results</h3>
        {searchResults ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Results Count:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{searchResults.items?.length || 0}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Total Matches:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1">{searchResults.total || 0}</div>
              </div>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Raw Data:</span>
              <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-40 mt-1">
                {formatJson(searchResults)}
              </pre>
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground">No search results available (try searching first)</div>
        )}
      </div>

      {/* API Endpoints Reference */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">🔌 API Endpoints</h3>
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium text-muted-foreground">Available tRPC Procedures:</span>
            <div className="mt-2 space-y-1">
              {[
                "multiLanguageTest.listProjects",
                "multiLanguageTest.createProject",
                "multiLanguageTest.getProject",
                "multiLanguageTest.updateProject",
                "multiLanguageTest.listTasks",
                "multiLanguageTest.createTask",
                "multiLanguageTest.updateTask",
                "multiLanguageTest.searchTasks",
                "multiLanguageTest.listComments",
                "multiLanguageTest.createComment",
                "multiLanguageTest.listProjectMembers",
                "multiLanguageTest.getDebugInfo",
              ].map((endpoint) => (
                <div key={endpoint} className="font-mono text-xs bg-muted p-2 rounded">
                  {endpoint}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Test URLs */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">🧪 Test URLs</h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-muted-foreground">Development Server:</span>
            <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
              http://localhost:8790/api/trpc/multiLanguageTest.getDebugInfo
            </div>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Example curl command:</span>
            <pre className="bg-muted p-3 rounded text-xs overflow-auto mt-1">
              {`curl "http://localhost:8790/api/trpc/multiLanguageTest.listProjects" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer lomin7"`}
            </pre>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4 text-foreground">📊 Performance Info</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-muted-foreground">Component Renders:</span>
            <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
              {Math.floor(Math.random() * 100) + 1} (simulated)
            </div>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Memory Usage:</span>
            <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
              {(Math.random() * 50 + 10).toFixed(1)} MB (simulated)
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DebugTab
